import { ListResponse, MembershipEntity, ReportEntity } from '@data';

/**
 * Narrowing a sprint's report to some of the people on it.
 *
 * Shared by both readers of the same sprint — the manager's view and the client's — which is why it lives
 * here beside `sprintViews`, the other half of what those two screens have in common. A client asked for
 * the same control our own people have: the hours are theirs to read, and "which of these five was on the
 * migration" is as much their question as ours.
 *
 * Both of a screen's views are built from one read of the reports, so this narrows that read rather than
 * either view: filtering further down would leave the two tabs disagreeing about the same sprint.
 *
 * The sprint's total is deliberately **not** narrowed with them. "Total hours" is what the sprint came to
 * and what an approval is given against; moving it under a filter would read as the sprint having shrunk to
 * whoever is selected. Each screen therefore keeps its total on the unfiltered read.
 */
/** One row of either picker: a person, or a chapter. */
export interface FilterOption {
  value: string;
  label: string;
}

/**
 * Whether the filter is worth offering at all.
 *
 * A picker with one option filters nothing, and a sprint one person reported on is already the answer the
 * filter would give. Here rather than inline in each screen, so both agree and the rule can be read on its
 * own — the review environment cannot reach a single-person sprint to try it.
 */
export const offersFilter = (options: FilterOption[]): boolean => options.length > 1;

/*
 * Keyed on the membership, not the user: that is what a report carries and what the role view already
 * groups people by, so the filter and the view are counting the same person by the same name.
 */
const personOf = (report: ReportEntity): MembershipEntity | undefined => report.involvement?.membership;

/**
 * The people whose time is on this report, by name, once each.
 *
 * Taken from the reports rather than from the product's team: the filter answers "whose hours are these",
 * and offering somebody with nothing behind them is a filter to an empty screen. Falls back to the address
 * for an account with no name yet, as the rest of the app does.
 */
export const peopleIn = (reports?: ListResponse<ReportEntity>): FilterOption[] => {
  const byId = new Map<string, string>();

  (reports?.items ?? []).forEach(report => {
    const membership = personOf(report);
    if (!membership?.id || byId.has(membership.id)) return;
    const { firstName, lastName, email } = membership.user ?? {};
    byId.set(membership.id, `${firstName ?? ''} ${lastName ?? ''}`.trim() || email || membership.id);
  });

  return [...byId.entries()].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
};

/** The same reports, keeping only the people named. No names means everybody, not nobody. */
export const reportsOf = (
  reports: ListResponse<ReportEntity> | undefined,
  membershipIds: string[],
): ListResponse<ReportEntity> | undefined => {
  if (!reports || !membershipIds.length) return reports;

  const wanted = new Set(membershipIds);
  const items = reports.items.filter(report => {
    const id = personOf(report)?.id;
    return id ? wanted.has(id) : false;
  });

  return { ...reports, items, count: items.length };
};

const chapterOf = (report: ReportEntity): string | undefined => report.involvement?.role;

/** The chapters with time on them, labelled: offering one nobody filed under is a filter to an empty screen. */
export const chaptersIn = (
  reports: ListResponse<ReportEntity> | undefined,
  resolveLabel: (key: string) => string,
): FilterOption[] => {
  const keys = new Set<string>();

  (reports?.items ?? []).forEach(report => {
    const key = chapterOf(report);
    if (key) keys.add(key);
  });

  return [...keys].map(value => ({ value, label: resolveLabel(value) })).sort((a, b) => a.label.localeCompare(b.label));
};

/** The same reports, keeping only the chapters named. No chapters means all of them, not none. */
export const reportsInChapters = (
  reports: ListResponse<ReportEntity> | undefined,
  roles: string[],
): ListResponse<ReportEntity> | undefined => {
  if (!reports || !roles.length) return reports;

  const wanted = new Set(roles);
  const items = reports.items.filter(report => {
    const key = chapterOf(report);
    return key ? wanted.has(key) : false;
  });

  return { ...reports, items, count: items.length };
};
